import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ArrowRight, Clock, BookOpen, Mail } from "lucide-react";
import { BLOG_POSTS } from "@/lib/blogPosts";

function formatDate(iso) {
  try {
    return new Date(iso).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });
  } catch { return iso; }
}

export default function Blog() {
  useEffect(() => {
    const prevTitle = document.title;
    document.title = "RiskEye Blog · Risk register & M365 playbooks for risk teams";
    const desc = document.querySelector('meta[name="description"]');
    const prevDesc = desc?.getAttribute("content");
    desc?.setAttribute("content", "Practical playbooks for digital risk registers, Microsoft 365 CIS assessment, risk appetite and board reporting — written by seQure practitioners. Short, opinionated, zero fluff.");
    const link = document.querySelector('link[rel="canonical"]');
    const prevHref = link?.getAttribute("href");
    link?.setAttribute("href", "https://riskeye.io/blog");
    return () => {
      document.title = prevTitle;
      if (desc && prevDesc != null) desc.setAttribute("content", prevDesc);
      if (link && prevHref != null) link.setAttribute("href", prevHref);
    };
  }, []);

  return (
    <div className="min-h-screen bg-[#FBFBF9] text-slate-900 font-sans" data-testid="blog-page">
      {/* Nav */}
      <header className="sticky top-0 z-40 bg-white/85 backdrop-blur-md border-b border-slate-200/70">
        <div className="max-w-6xl mx-auto flex items-center justify-between px-6 py-3.5">
          <Link to="/" className="flex items-center gap-2.5" data-testid="blog-brand">
            <div className="h-8 w-8 rounded-md bg-gradient-to-br from-rose-500 via-amber-500 to-emerald-500 p-[1.5px]">
              <div className="h-full w-full rounded-[5px] bg-[#050B1F] flex items-center justify-center">
                <div className="h-1.5 w-1.5 rounded-full bg-rose-400" />
              </div>
            </div>
            <div className="leading-tight">
              <div className="font-display font-bold text-[19px] tracking-tight leading-none">RiskEye</div>
              <div className="text-[9.5px] font-mono uppercase tracking-[0.22em] text-slate-500 mt-0.5">by seQure</div>
            </div>
          </Link>
          <Link to="/" className="inline-flex items-center gap-1.5 text-[13.5px] text-slate-600 hover:text-slate-900" data-testid="blog-back">
            <ArrowLeft className="h-3.5 w-3.5" /> Back to home
          </Link>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none" style={{
          background: "radial-gradient(45% 55% at 85% 10%, rgba(6, 182, 212, 0.10), transparent 70%), radial-gradient(45% 45% at 5% 95%, rgba(244, 63, 94, 0.08), transparent 70%)",
        }} />
        <div className="relative max-w-4xl mx-auto px-6 pt-16 md:pt-24 pb-10">
          <div className="inline-flex items-center gap-2 bg-white border border-slate-200 rounded-full px-3 py-1 text-[11.5px] font-mono uppercase tracking-[0.18em] text-slate-500 shadow-sm mb-6">
            <BookOpen className="h-3 w-3 text-cyan-600" /> RiskEye Insights
          </div>
          <h1 className="font-display font-bold text-[42px] md:text-[54px] leading-[1.05] tracking-tight text-slate-900">
            Playbooks for risk teams<br/>
            <span className="bg-gradient-to-br from-cyan-700 via-cyan-600 to-emerald-600 bg-clip-text text-transparent">
              who value visibility over paperwork.
            </span>
          </h1>
          <p className="mt-6 text-[16px] md:text-[17px] text-slate-600 leading-relaxed max-w-2xl">
            Short, opinionated guides from the seQure advisory practice - the same content our advisors walk customers through on day one. No gated PDFs. No lead-capture. Just the working.
          </p>
        </div>
      </section>

      {/* Post grid */}
      <section className="max-w-6xl mx-auto px-6 pb-24" data-testid="blog-list">
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
          {BLOG_POSTS.map((p) => (
            <Link
              key={p.slug}
              to={`/blog/${p.slug}`}
              className="group flex flex-col rounded-2xl border border-slate-200 bg-white p-6 hover:border-slate-900 hover:shadow-[0_16px_40px_-20px_rgba(15,23,42,0.35)] transition-all"
              data-testid={`blog-card-${p.slug}`}
            >
              <div className="flex items-center gap-2 text-[10.5px] font-mono uppercase tracking-[0.24em] text-cyan-700 font-semibold">
                <span>{p.kicker}</span>
                <span className="text-slate-300">·</span>
                <span className="text-slate-500">{p.tag}</span>
              </div>
              <h2 className="mt-3 font-display font-semibold text-[19px] tracking-tight leading-snug text-slate-900 group-hover:text-cyan-800 transition-colors">{p.title}</h2>
              <p className="mt-3 text-[13.5px] text-slate-600 leading-relaxed">{p.excerpt}</p>
              <div className="mt-auto pt-5 flex items-center justify-between text-[11.5px] font-mono text-slate-500 border-t border-slate-100">
                <span>{formatDate(p.published)}</span>
                <span className="flex items-center gap-1"><Clock className="h-3 w-3" /> {p.read_minutes} min read</span>
              </div>
              <div className="mt-3 inline-flex items-center gap-1.5 text-[12.5px] font-semibold text-slate-800 group-hover:text-cyan-800 transition-colors">
                Read the guide <ArrowRight className="h-3.5 w-3.5 group-hover:translate-x-0.5 transition-transform" />
              </div>
            </Link>
          ))}
        </div>

        {/* Newsletter / contact strip */}
        <div className="mt-14 rounded-2xl bg-[#050B1F] p-8 md:p-10 text-slate-100 relative overflow-hidden">
          <div className="absolute inset-0 opacity-40 pointer-events-none" style={{
            background: "radial-gradient(45% 45% at 15% 15%, rgba(6, 182, 212, 0.22), transparent 70%), radial-gradient(35% 35% at 90% 90%, rgba(16, 185, 129, 0.16), transparent 70%)",
          }} />
          <div className="relative grid md:grid-cols-[1.4fr_1fr] gap-8 items-center">
            <div>
              <div className="text-[10.5px] font-mono uppercase tracking-[0.28em] text-cyan-300 mb-3">Want more?</div>
              <div className="font-display font-bold text-[24px] md:text-[28px] tracking-tight leading-tight text-white">Send us the messy risk problem your team is stuck on.</div>
              <p className="mt-3 text-[14px] text-slate-300 leading-relaxed max-w-xl">We'll write the next guide against a real question. If you're running into an appetite fight, a framework mapping headache, or a board pack that won't land - hit reply.</p>
              <a
                href="/blog.xml"
                target="_blank"
                rel="noopener noreferrer"
                className="mt-4 inline-flex items-center gap-1.5 text-[11.5px] font-mono uppercase tracking-[0.2em] text-cyan-300 hover:text-white transition-colors"
                data-testid="blog-rss-link"
              >
                Subscribe · RSS feed →
              </a>
            </div>
            <a
              href="mailto:hello@riskeye.io"
              className="inline-flex items-center justify-center gap-2 bg-white text-slate-900 hover:bg-slate-100 font-medium text-[14px] px-5 py-3 rounded-md transition-colors"
              data-testid="blog-cta-email"
            >
              <Mail className="h-4 w-4" /> hello@riskeye.io
            </a>
          </div>
        </div>
      </section>

      {/* Footer strip */}
      <footer className="border-t border-slate-200 bg-white">
        <div className="max-w-6xl mx-auto px-6 py-6 flex flex-col md:flex-row items-center justify-between gap-3 text-[11.5px] font-mono uppercase tracking-[0.22em] text-slate-400">
          <span>© {new Date().getFullYear()} RiskEye · by seQure</span>
          <div className="flex items-center gap-5">
            <Link to="/" className="hover:text-slate-800">Home</Link>
            <Link to="/about" className="hover:text-slate-800">About</Link>
            <Link to="/trust" className="hover:text-slate-800">Trust</Link>
            <a href="mailto:hello@riskeye.io" className="hover:text-slate-800">Contact</a>
          </div>
        </div>
      </footer>
    </div>
  );
}
